import { Button, Col, Drawer, Row, Typography } from 'antd';
import moment from 'moment';
import React, { useState, useMemo } from 'react';
import ClosedConfirmationModal from '~/Components/Modal/ClosedConfirmationModal';
import { Action } from '~/Components/TableDrawer/TableDrawer';
import { renderPublishedColumn } from '~/Utils/TableRenderFunction';

const { Title, Paragraph } = Typography;

interface PassengerInfo {
  fullName: string;
  nationality:string;
  pno: string;
  pic: string;
  age: number;
  gender: string;
  contact: string;
  address1: string;
  address2: string;
  city: string;
  postalCode: string;
  country: string;
  email: string;
  contactCountryCode:string;
}

interface GuardianInfo {
  fullName: string;
  pno: string;
  pic: string;
  age: number;
  gender: string;
  contact: string;
  address1: string;
  address2: string;
  city: string;
  postalCode: string;
  country: string;
  email: string;
  contactCountryCode:string;
  relation?: string
}

interface SelectedRowData {
  requestId: string;
  airport: string;
  airline: string;
  flight_No: string;
  DOV: string;
  createdAt: string;
  updatedAt: string;
  updatedBy: string;
  attendedBy: string;
  email: string;
  status: string;
  comment: string;
  acknowledgement: boolean | null;
  responsibleForSupervision: boolean | null;
  permissionToEscort: boolean | null;
  disablePassenger: PassengerInfo;
  guardian: GuardianInfo;
  flightStatus:string;
  airportArrivalTime:string;
  requireCalmRoomUsage:boolean | null;
  recurringUser:boolean | null;
}

interface ButterFlyTableDrawerProps {
  drawerVisible: boolean;
  setDrawerVisible: (visible: boolean) => void;
  selectedRowData: SelectedRowData | null; 
  actions: Action[]
}

export const ButterFlyTableDrawer: React.FC<ButterFlyTableDrawerProps> = ({
  drawerVisible,
  setDrawerVisible,
  selectedRowData,
  actions
}) => {
  const [closeModalVisible, setCloseModalVisible] = useState(false);
  const [onClickFunction, setOnClickFunction] = useState<Function>();
  const closeRequest = async (status: string, attenderName: string, reason: string) => {
    if (onClickFunction) {
      try {
        await onClickFunction(status, attenderName, reason );
      } catch (err) {
        console.error("Error submitting the request:", err);
      }
    }
  };
  const formatDate = (date: string | undefined, format: string): string => {
    return date ? moment(date).format(format) : 'N/A';
  };

  const statusFormat =(status: string | undefined)=>{
    switch (status) {
      case 'noShow':
        return 'No Show';
      case 'complete':
        return 'Complete';
      case 'cancel':
        return 'Cancel';
      default:
        return status; 
    }
  }

  const formattedDOV = useMemo(() => formatDate(selectedRowData?.DOV, 'DD MMM YYYY hh:mm:ss A'), [selectedRowData]);
  const formattedArrivalDate = useMemo(() => formatDate(selectedRowData?.airportArrivalTime, 'DD MMM YYYY hh:mm:ss A'), [selectedRowData]);
  const formattedCreatedAt = useMemo(() => formatDate(selectedRowData?.createdAt, 'DD MMM YYYY hh:mm:ss A'), [selectedRowData]);
  const formattedUpdatedAt = useMemo(() => formatDate(selectedRowData?.updatedAt, 'DD MMM YYYY hh:mm:ss A'), [selectedRowData]);
  const renderField = (title: string, value: string | number | undefined | undefined | boolean) => {
   const displayValue =
    typeof value === "boolean" ? (value ? "Yes" : "No") : value || "N/A";
    return(
    <Col span={12}>
      <Title level={5}>{title}</Title>
      <Paragraph>{displayValue}</Paragraph>
    </Col>
  )};

  return (
    <>
      <ClosedConfirmationModal
        type="close"
        open={closeModalVisible}
        onClose={() => setCloseModalVisible(false)}
        onSubmit={closeRequest}
      />

      <Drawer
        title="Butterfly Effect Guest Registration Details"
        placement="right"
        open={drawerVisible}
        onClose={() => setDrawerVisible(false)}
        width={600}
      >
        {selectedRowData?.comment && (
          <>
            <Typography>
              <Title level={4}>Comment</Title>
              <Paragraph>{selectedRowData.comment}</Paragraph>
            </Typography>
          </>
        )}
        {
          (selectedRowData?.status === 'closed' ||selectedRowData?.status === 'cancel' || selectedRowData?.status === 'noShow' ||selectedRowData?.status === 'complete')&& (
            <>
          <Typography>
            <Row gutter={[16, 16]}>
              {renderField('Closed By', selectedRowData.updatedBy)}
              {renderField('Closed At', formattedUpdatedAt)}
            </Row>
          </Typography>
          <Typography>
            <Row gutter={[16, 16]}>
              {renderField('Attended By', selectedRowData.attendedBy)}
              {renderField('Closed Status', statusFormat(selectedRowData?.status))}
            </Row>
          </Typography>
            <hr className="my-5 border-gray-300" />
            </>
          
          )
          
        }
        {selectedRowData && (
          <Typography>
            <Title level={4}>Details of Visit</Title>
            <Row gutter={[16, 16]}>
              {renderField('Flight Number', selectedRowData.flight_No)}
              {renderField('Travel Date & Time', formattedDOV)}
              {renderField('Flight Status', selectedRowData.flightStatus)}
              {renderField('Airport', selectedRowData.airport)}
              {/* {renderField('Airport Arrival Date & Time', formattedArrivalDate)} */}
              {renderField('Do you require the usage of our Calm Room', selectedRowData?.requireCalmRoomUsage)}
              {renderField('I am a recurring user', selectedRowData?.recurringUser)}      
            </Row>

            <Title level={4}>Hidden Disability Passenger’s Details</Title>
            <Row gutter={[16, 16]}>
              {renderField('Full Name', selectedRowData.disablePassenger.fullName)}
              {renderField('Age', selectedRowData.disablePassenger.age)}
              {renderField('Gender', selectedRowData.disablePassenger.gender)}
              {renderField('NRIC/Passport Number', selectedRowData.disablePassenger.pno)}
              {renderField('Nationality', selectedRowData.disablePassenger.nationality)}
              {renderField('Contact Number',selectedRowData.disablePassenger.contactCountryCode+" "+selectedRowData.disablePassenger.contact)}
            </Row>

            <Title level={4}>Emergency Contact Details</Title>
            <Row gutter={[16, 16]}>
              {renderField('Full Name', selectedRowData.guardian.fullName)}
              {renderField('Relationship', selectedRowData.guardian?.relation)}
              {renderField('Contact Number', selectedRowData.guardian.contactCountryCode+" "+selectedRowData.guardian.contact)}
            </Row>

            <Title level={4}>Email Details</Title>
            <Row gutter={[16, 16]}>
              {renderField('Email', selectedRowData.email)}
            </Row>

            <Title level={4}>Acknowledgement</Title>
            <Col span={12}>
              <Paragraph>Yes</Paragraph>
            </Col>
          </Typography>
        )}

        <div className="mt-5 flex flex-row-reverse gap-5">
          {actions.map((action, index) => (
            <Button
              key={index}
              onClick={() => {
                setOnClickFunction(() => action.onClick);
                if (action.label === 'Close Request') {
                  setCloseModalVisible(true);
                }
              }}
              type={action.type || 'default'}
            >
              {action.label}
            </Button>
          ))}
        </div>
      </Drawer>
    </>
  );
};
